import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { type CompanyDetailType, useCompanySummaryById } from '@/api/activity';
import { Legend, LegendItem } from '@/components/Legend';
import { Panel } from '@/components/panels';
import { getChannelColor } from '@/constants/colors/channel';

import CompanyActivityLists from './CompanyActivityLists';
import CompanyVisitChartCaption from './CompanyVisitChartCaption';
import CompanyVisitDonutChart from './CompanyVisitDonutChart';

type Props = {
  open: boolean;
  data: CompanyDetailType | null;
  startDate?: string;
  endDate?: string;
};

const CompanyTotalVisitsPanel = ({ open, data, startDate, endDate }: Props) => {
  const { companySummary, isLoading, error } = useCompanySummaryById(data?.id, startDate, endDate);
  const [disabledSeries, setDisabledSeries] = useState<string[] | undefined>([]);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisabledSeries([]);
    }
  }, [open]);

  const numDays = dayjs(endDate).diff(dayjs(startDate), 'days');

  return (
    <Panel title={<Trans>Total Visits ({numDays} Days)</Trans>} noPadding>
      <Legend disabledValues={disabledSeries} onDisabledSeriesChange={setDisabledSeries}>
        {companySummary?.trafficVisitBreakdown.map((d) => (
          <LegendItem
            key={d.vendor.id}
            value={d.vendor.id}
            color={getChannelColor(d.vendor.channel)}
            name={d.vendor.displayName}
          />
        ))}
      </Legend>
      <div className="flex items-start gap-4">
        <CompanyVisitDonutChart
          data={companySummary?.trafficVisitBreakdown}
          isLoading={isLoading}
          error={error}
          disabledSeries={disabledSeries}
        />
        <CompanyVisitChartCaption data={companySummary?.companySummary} />
      </div>
      <CompanyActivityLists data={companySummary?.companySummary} />
    </Panel>
  );
};

export default CompanyTotalVisitsPanel;
